import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { db, rtdb } from '../firebase/config';
import { doc, collection, query, where, onSnapshot, updateDoc, getDoc } from 'firebase/firestore';
import { ref, onValue } from 'firebase/database';
import PostCard from '../components/PostCard';
import { 
    UserPlus, 
    UserCheck, 
    MessageCircle, 
    ArrowLeft, 
    LogOut, 
    Edit3, 
    Check, 
    Copy, 
    UserMinus, 
    Clock, 
    Users, 
    MessageSquare,
    AlertCircle,
    Settings,
    X
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const ProfilePage = ({ navigate, userId }) => {
    const { 
        currentUser, 
        userProfile, 
        sendFriendRequest, 
        cancelFriendRequest, 
        acceptFriendRequest, 
        removeFriend, 
        updateUsername,
        signOut 
    } = useAuth();
    
    const targetUserId = userId || currentUser?.uid;
    const isMe = currentUser && currentUser.uid === targetUserId;

    const [profileData, setProfileData] = useState(null);
    const [userPosts, setUserPosts] = useState([]);
    const [friendsList, setFriendsList] = useState([]);
    const [activeTab, setActiveTab] = useState('thoughts'); // 'thoughts' | 'friends'
    const [isOnline, setIsOnline] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isEditingBio, setIsEditingBio] = useState(false);
    const [bioText, setBioText] = useState('');
    const [isEditingUsername, setIsEditingUsername] = useState(false);
    const [usernameText, setUsernameText] = useState('');
    const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
    const [userToRemove, setUserToRemove] = useState(null); // for 2-click confirmation modal
    const [copiedId, setCopiedId] = useState(false);

    const isFriend = userProfile?.friends?.includes(targetUserId);
    const hasSentRequest = userProfile?.outgoingFriendRequests?.includes(targetUserId);
    const hasIncomingRequest = userProfile?.incomingFriendRequests?.includes(targetUserId);

    // Fetch user profile doc
    useEffect(() => {
        if (!targetUserId) return;
        const userRef = doc(db, 'users', targetUserId);
        const unsub = onSnapshot(userRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setProfileData(data);
                setBioText(data.bio || '');
                setUsernameText(data.username || '');
            } else {
                setProfileData(null);
            }
            setLoading(false);
        }, (err) => {
            console.error("Profile fetch error:", err);
            setLoading(false);
        });

        return () => unsub();
    }, [targetUserId]);

    const handleSaveUsername = async () => {
        if (!isMe || !usernameText.trim()) return;
        try {
            await updateUsername(usernameText.trim());
            setIsEditingUsername(false);
        } catch (e) {
            // Toast already shown in updateUsername
        }
    };

    // Fetch friends profiles for display on profile
    useEffect(() => {
        const friendIds = profileData?.friends || [];
        if (!friendIds.length) {
            setFriendsList([]);
            return;
        }

        let isSubscribed = true;
        const fetchFriends = async () => {
            try {
                const docs = await Promise.all(friendIds.slice(0, 50).map(async (fid) => {
                    const snap = await getDoc(doc(db, 'users', fid));
                    if (snap.exists()) {
                        return { id: snap.id, ...snap.data() };
                    }
                    return { id: fid, displayName: 'Batcheet User', username: 'user' };
                }));
                if (isSubscribed) {
                    setFriendsList(docs);
                }
            } catch (err) {
                console.error("Failed to load friends details:", err);
            }
        };

        fetchFriends();
        return () => { isSubscribed = false; };
    }, [profileData?.friends]);

    // Fetch user's active posts (within 24h)
    useEffect(() => {
        if (!targetUserId) return;
        const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
        const q = query(
            collection(db, 'posts'),
            where('authorId', '==', targetUserId)
        );

        const unsubPosts = onSnapshot(q, (snapshot) => {
            const posts = snapshot.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(p => !p.createdAt?.toDate || p.createdAt.toDate() > twentyFourHoursAgo)
                .sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
            setUserPosts(posts);
        });

        return () => unsubPosts();
    }, [targetUserId]);

    // Live presence
    useEffect(() => {
        if (!rtdb || !targetUserId) return;
        const statusRef = ref(rtdb, `/status/${targetUserId}`);
        const unsubPresence = onValue(statusRef, (snap) => {
            setIsOnline(snap.val()?.state === 'online');
        });
        return () => unsubPresence();
    }, [targetUserId]);

    const handleSaveBio = async () => {
        if (!isMe) return;
        try {
            await updateDoc(doc(db, 'users', currentUser.uid), {
                bio: bioText.trim()
            });
            setIsEditingBio(false);
            toast.success("Bio updated!");
        } catch (e) {
            toast.error("Failed to update bio.");
        }
    };

    const handleStartDirectChat = (id = targetUserId) => {
        if (!currentUser || currentUser.uid === id) return;
        const chatId = [currentUser.uid, id].sort().join('_');
        navigate('chat', { chatId });
    };

    const handleCopyId = (idToCopy, label = "User ID") => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(idToCopy);
            setCopiedId(idToCopy);
            toast.success(`${label} copied to clipboard! 📋`);
            setTimeout(() => setCopiedId(null), 2500);
        }
    };

    const confirmRemoveFriend = async () => {
        if (!userToRemove) return;
        await removeFriend(userToRemove.id);
        setUserToRemove(null);
    };

    if (loading) {
        return (
            <div className="py-16 text-center space-y-3">
                <div className="w-10 h-10 mx-auto rounded-full border-2 border-sky-500 border-t-transparent animate-spin" />
                <p className="text-xs text-slate-400">Loading profile...</p>
            </div>
        );
    }

    if (!profileData) {
        return (
            <div className="text-center py-20 bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-3xl p-8 space-y-4 shadow-sm">
                <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200">User Not Found</h2>
                <button onClick={() => navigate('home')} className="text-xs text-sky-500 dark:text-sky-400 underline font-semibold">
                    Return to feed
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-4 sm:space-y-5 animate-fade-in pb-16">
            <button
                onClick={() => navigate('home')}
                className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400 hover:text-sky-500 dark:hover:text-sky-400 transition-colors p-1"
            >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to feed</span>
            </button>

            {/* Profile Hero Card */}
            <div className="relative bg-white dark:bg-[#111827]/80 backdrop-blur-md border border-slate-200/90 dark:border-slate-800/80 rounded-2xl sm:rounded-3xl shadow-xs overflow-hidden">
                {/* Clean Gradient Cover Banner */}
                <div className="h-28 sm:h-32 bg-gradient-to-r from-sky-500 via-indigo-500 to-purple-600 relative overflow-hidden">
                    <div className="absolute inset-0 bg-black/10"></div>
                </div>

                <div className="px-4 sm:px-6 pb-5 sm:pb-6 pt-0 relative">
                    {/* Avatar with Status Pulse */}
                    <div className="flex justify-between items-end -mt-10 sm:-mt-12 mb-3.5 sm:mb-4">
                        <div className="relative">
                            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full p-1 bg-white dark:bg-[#111827] shadow-sm">
                                <div className="w-full h-full rounded-full p-[2px] bg-gradient-to-tr from-sky-400 to-indigo-500">
                                    <img
                                        src={profileData.photoURL || `https://api.dicebear.com/7.x/bottts/svg?seed=${targetUserId}`}
                                        alt=""
                                        className="w-full h-full rounded-full object-cover bg-slate-100 dark:bg-slate-800"
                                    />
                                </div>
                            </div>
                            {isOnline && (
                                <span className="absolute bottom-1 right-1 w-4 h-4 sm:w-4.5 sm:h-4.5 bg-emerald-500 border-2 sm:border-3 border-white dark:border-[#111827] rounded-full shadow-xs"></span>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                            {isMe ? (
                                <>
                                    <button
                                        onClick={() => navigate('settings')}
                                        className="px-3 sm:px-3.5 py-1.5 sm:py-2 bg-slate-100 dark:bg-slate-800/80 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all active:scale-95"
                                    >
                                        <Settings className="w-3.5 h-3.5 text-sky-500" />
                                        <span>Settings</span>
                                    </button>
                                    <button
                                        onClick={() => setShowSignOutConfirm(true)}
                                        className="px-3 sm:px-3.5 py-1.5 sm:py-2 bg-slate-100 dark:bg-slate-800/80 hover:bg-rose-500/10 hover:text-rose-500 dark:hover:bg-rose-500/20 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all active:scale-95"
                                    >
                                        <LogOut className="w-3.5 h-3.5 text-rose-500" />
                                        <span>Sign Out</span>
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={() => handleStartDirectChat(targetUserId)}
                                        className="px-3.5 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-sky-500 to-indigo-600 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-sky-500/20 hover:scale-102 active:scale-98 transition-all"
                                    >
                                        <MessageCircle className="w-4 h-4" />
                                        <span>Message</span>
                                    </button>

                                    {isFriend ? (
                                        <button
                                            onClick={() => setUserToRemove({ id: targetUserId, displayName: profileData.displayName, username: profileData.username })}
                                            className="px-3.5 sm:px-4 py-1.5 sm:py-2 bg-sky-500/10 dark:bg-sky-500/20 text-sky-600 dark:text-sky-400 border border-sky-500/30 hover:bg-rose-500/10 hover:text-rose-500 hover:border-rose-500/30 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all group"
                                            title="Click to remove friend"
                                        >
                                            <UserCheck className="w-4 h-4 group-hover:hidden" />
                                            <UserMinus className="w-4 h-4 hidden group-hover:inline-block" />
                                            <span className="group-hover:hidden">Friends</span>
                                            <span className="hidden group-hover:inline">Remove</span>
                                        </button>
                                    ) : hasIncomingRequest ? (
                                        <button
                                            onClick={() => acceptFriendRequest(targetUserId)}
                                            className="px-3.5 sm:px-4 py-1.5 sm:py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm transition-all"
                                        >
                                            <UserPlus className="w-4 h-4" />
                                            <span>Accept</span>
                                        </button>
                                    ) : hasSentRequest ? (
                                        <button
                                            onClick={() => cancelFriendRequest(targetUserId)}
                                            className="px-3.5 sm:px-4 py-1.5 sm:py-2 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-rose-500/10 hover:text-rose-500 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all"
                                            title="Click to cancel friend request"
                                        >
                                            <Clock className="w-4 h-4 animate-spin-slow" />
                                            <span>Requested</span>
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => sendFriendRequest(targetUserId)}
                                            className="px-3.5 sm:px-4 py-1.5 sm:py-2 bg-slate-900 dark:bg-white hover:bg-slate-800 dark:hover:bg-slate-100 text-white dark:text-slate-900 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all"
                                        >
                                            <UserPlus className="w-4 h-4" />
                                            <span>Add Friend</span>
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Names, Handle, and User ID */}
                    <div className="space-y-1">
                        <div className="flex items-center gap-2">
                            <h2 className="text-lg sm:text-xl font-black text-slate-900 dark:text-white">
                                {profileData.displayName || 'User'}
                            </h2>
                            {isOnline && (
                                <span className="text-[10px] sm:text-[11px] font-semibold text-emerald-500 dark:text-emerald-400">● Online</span>
                            )}
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-2">
                            {isEditingUsername ? (
                                <div className="flex items-center gap-1.5">
                                    <span className="text-xs font-mono font-bold text-sky-500">@</span>
                                    <input
                                        type="text"
                                        maxLength="20"
                                        value={usernameText}
                                        onChange={(e) => setUsernameText(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                                        className="w-28 sm:w-36 px-2 py-0.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-mono text-slate-900 dark:text-slate-100 focus:outline-none"
                                        placeholder="username"
                                    />
                                    <button
                                        onClick={handleSaveUsername}
                                        className="p-1 bg-sky-500 text-white rounded-md text-xs font-bold"
                                        title="Save username"
                                    >
                                        <Check className="w-3 h-3" />
                                    </button>
                                    <button
                                        onClick={() => { setIsEditingUsername(false); setUsernameText(profileData.username || ''); }}
                                        className="p-1 bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-md text-xs font-bold"
                                        title="Cancel"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-1 group/handle">
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-mono">@{profileData.username || 'user'}</p>
                                    {isMe && (
                                        <button
                                            onClick={() => { setIsEditingUsername(true); setUsernameText(profileData.username || ''); }}
                                            className="text-slate-400 hover:text-sky-500 opacity-0 group-hover/handle:opacity-100 transition-opacity p-0.5"
                                            title="Change username"
                                        >
                                            <Edit3 className="w-2.5 h-2.5" />
                                        </button>
                                    )}
                                </div>
                            )}
                            
                            {/* User ID with 1-tap copy */}
                            <button
                                onClick={() => handleCopyId(targetUserId, isMe ? "Your User ID" : "User ID")}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800/90 text-slate-500 dark:text-slate-400 hover:text-sky-500 dark:hover:text-sky-400 text-[11px] font-mono transition-colors border border-slate-200/60 dark:border-slate-700/60"
                                title="Click to copy User ID"
                            >
                                <span className="text-[10px] opacity-60">ID:</span>
                                <span>{targetUserId.slice(0, 6)}...{targetUserId.slice(-4)}</span>
                                {copiedId === targetUserId ? (
                                    <Check className="w-3 h-3 text-emerald-500" />
                                ) : (
                                    <Copy className="w-3 h-3" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Bio Section */}
                    <div className="mt-2.5 sm:mt-3">
                        {isEditingBio ? (
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    maxLength="120"
                                    value={bioText}
                                    onChange={(e) => setBioText(e.target.value)}
                                    className="flex-1 px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-slate-100 focus:outline-none"
                                />
                                <button
                                    onClick={handleSaveBio}
                                    className="p-1.5 sm:p-2 bg-sky-500 text-white rounded-xl text-xs font-bold"
                                >
                                    <Check className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 group/bio">
                                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed font-normal">
                                    {profileData.bio || 'Exploring the 24h fleeting vibe.'}
                                </p>
                                {isMe && (
                                    <button
                                        onClick={() => setIsEditingBio(true)}
                                        className="text-slate-400 hover:text-sky-500 opacity-0 group-hover/bio:opacity-100 transition-opacity p-1"
                                    >
                                        <Edit3 className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Stats Bar with Interactive Tab Switch */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 pt-4 sm:pt-5 mt-3 sm:mt-4 border-t border-slate-100 dark:border-slate-800/80">
                        <button
                            onClick={() => setActiveTab('friends')}
                            className={`p-2.5 sm:p-3 rounded-2xl text-center transition-all ${
                                activeTab === 'friends' 
                                    ? 'bg-sky-500/10 border border-sky-500/30' 
                                    : 'bg-slate-50/80 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-800/50'
                            }`}
                        >
                            <span className="text-base sm:text-lg font-black text-sky-500 dark:text-sky-400">
                                {profileData.friends?.length || 0}
                            </span>
                            <p className="text-[10px] sm:text-[11px] font-semibold text-slate-500 dark:text-slate-400">Friends & IDs</p>
                        </button>
                        <button
                            onClick={() => setActiveTab('thoughts')}
                            className={`p-2.5 sm:p-3 rounded-2xl text-center transition-all ${
                                activeTab === 'thoughts' 
                                    ? 'bg-sky-500/10 border border-sky-500/30' 
                                    : 'bg-slate-50/80 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-800/50'
                            }`}
                        >
                            <span className="text-base sm:text-lg font-black text-indigo-500 dark:text-indigo-400">{userPosts.length}</span>
                            <p className="text-[10px] sm:text-[11px] font-semibold text-slate-500 dark:text-slate-400">Active Thoughts</p>
                        </button>
                        <div className="p-2.5 sm:p-3 bg-slate-50/80 dark:bg-slate-900/50 rounded-2xl text-center hidden sm:block">
                            <span className="text-base sm:text-lg font-black text-amber-500 dark:text-amber-400">24H</span>
                            <p className="text-[10px] sm:text-[11px] font-semibold text-slate-500 dark:text-slate-400">Lifespan</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Segmented Navigation Tab */}
            <div className="flex items-center gap-1.5 p-1 bg-slate-200/60 dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-slate-800/80">
                <button
                    onClick={() => setActiveTab('thoughts')}
                    className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                        activeTab === 'thoughts'
                            ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs'
                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                >
                    <MessageSquare className="w-3.5 h-3.5" />
                    <span>Active Thoughts ({userPosts.length})</span>
                </button>
                <button
                    onClick={() => setActiveTab('friends')}
                    className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                        activeTab === 'friends'
                            ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs'
                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                >
                    <Users className="w-3.5 h-3.5" />
                    <span>Friends & IDs ({profileData.friends?.length || 0})</span>
                </button>
            </div>

            {/* Tab Contents */}
            {activeTab === 'thoughts' ? (
                /* User's Active Posts */
                <div className="space-y-4">
                    {userPosts.length > 0 ? (
                        <div className="space-y-4">
                            {userPosts.map(post => (
                                <PostCard key={post.id} post={post} navigate={navigate} />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 bg-white dark:bg-[#111827]/60 border border-dashed border-slate-200 dark:border-slate-800 rounded-3xl p-6">
                            <p className="text-xs text-slate-500 dark:text-slate-400">No active posts right now. Everything vanishes after 24 hours!</p>
                        </div>
                    )}
                </div>
            ) : (
                /* Friends & IDs List */
                <div className="space-y-3">
                    <div className="flex items-center justify-between px-1">
                        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                            {isMe ? "Your friends & their unique User IDs" : `${profileData.displayName}'s Friends`}
                        </span>
                        <span className="text-[11px] font-mono text-slate-400">
                            {friendsList.length} total
                        </span>
                    </div>

                    {friendsList.length > 0 ? (
                        <div className="grid grid-cols-1 gap-2.5">
                            {friendsList.map(friend => (
                                <div 
                                    key={friend.id}
                                    className="p-3 sm:p-3.5 bg-white dark:bg-[#111827]/80 backdrop-blur-sm border border-slate-200/90 dark:border-slate-800/80 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xs"
                                >
                                    {/* Friend Avatar & Details */}
                                    <div 
                                        onClick={() => navigate('profile', { userId: friend.id })}
                                        className="flex items-center gap-3 cursor-pointer group"
                                    >
                                        <img 
                                            src={friend.photoURL || `https://api.dicebear.com/7.x/bottts/svg?seed=${friend.id}`} 
                                            alt="" 
                                            className="w-10 h-10 rounded-full object-cover bg-slate-100 dark:bg-slate-800"
                                        />
                                        <div>
                                            <p className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white group-hover:text-sky-500 transition-colors">
                                                {friend.displayName || 'User'}
                                            </p>
                                            <p className="text-[11px] font-mono text-slate-500 dark:text-slate-400">
                                                @{friend.username || 'user'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Friend ID Pill & Actions */}
                                    <div className="flex items-center justify-between sm:justify-end gap-2 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100 dark:border-slate-800/60">
                                        {/* Friend ID Pill with 1-click copy */}
                                        <button
                                            onClick={() => handleCopyId(friend.id, `${friend.displayName}'s ID`)}
                                            className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 dark:bg-slate-800/90 hover:bg-slate-200 dark:hover:bg-slate-700/80 rounded-lg text-[11px] font-mono text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700/70 transition-all group/id"
                                            title="Click to copy Friend ID"
                                        >
                                            <span className="text-[10px] text-slate-400 font-sans">ID:</span>
                                            <span className="font-semibold">{friend.id.slice(0, 8)}...</span>
                                            {copiedId === friend.id ? (
                                                <Check className="w-3 h-3 text-emerald-500" />
                                            ) : (
                                                <Copy className="w-3 h-3 text-slate-400 group-hover/id:text-sky-500" />
                                            )}
                                        </button>

                                        <div className="flex items-center gap-1.5">
                                            {/* Quick Chat Button */}
                                            {friend.id !== currentUser?.uid && (
                                                <button
                                                    onClick={() => handleStartDirectChat(friend.id)}
                                                    className="p-1.5 bg-sky-500/10 hover:bg-sky-500/20 text-sky-600 dark:text-sky-400 rounded-lg transition-colors"
                                                    title="Message"
                                                >
                                                    <MessageCircle className="w-4 h-4" />
                                                </button>
                                            )}

                                            {/* Remove Friend Button (Triggers 2-click Confirmation) */}
                                            {isMe && (
                                                <button
                                                    onClick={() => setUserToRemove(friend)}
                                                    className="p-1.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-500 rounded-lg transition-colors"
                                                    title="Remove Friend"
                                                >
                                                    <UserMinus className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 bg-white dark:bg-[#111827]/60 border border-dashed border-slate-200 dark:border-slate-800 rounded-3xl p-6">
                            <p className="text-xs text-slate-500 dark:text-slate-400">No friends connected yet.</p>
                        </div>
                    )}
                </div>
            )}

            {/* 2-Click Friend Removal Confirmation Modal Portal */}
            {userToRemove && createPortal(
                <div 
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
                    onClick={() => setUserToRemove(null)}
                >
                    <div 
                        className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-2xl sm:rounded-3xl p-5 sm:p-6 w-full max-w-sm shadow-xl space-y-4 animate-scale-in"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="w-12 h-12 rounded-full bg-rose-500/10 text-rose-500 flex items-center justify-center mx-auto">
                            <AlertCircle className="w-6 h-6" />
                        </div>
                        
                        <div className="text-center space-y-1">
                            <h3 className="text-base font-bold text-slate-900 dark:text-white">
                                Remove Friend?
                            </h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                Are you sure you want to remove <span className="font-semibold text-slate-800 dark:text-slate-200">@{userToRemove.username}</span> from your friends list?
                            </p>
                        </div>

                        <div className="flex items-center gap-2 pt-2">
                            <button
                                onClick={() => setUserToRemove(null)}
                                className="flex-1 py-2.5 px-4 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-bold transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmRemoveFriend}
                                className="flex-1 py-2.5 px-4 bg-rose-500 hover:bg-rose-600 text-white rounded-xl text-xs font-bold transition-all shadow-sm shadow-rose-500/20"
                            >
                                Confirm Remove
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* 2-Click Sign Out Confirmation Modal Portal */}
            {showSignOutConfirm && createPortal(
                <div 
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
                    onClick={() => setShowSignOutConfirm(false)}
                >
                    <div 
                        className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-2xl sm:rounded-3xl p-5 sm:p-6 w-full max-w-sm shadow-xl space-y-4 animate-scale-in"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="w-12 h-12 rounded-full bg-rose-500/10 text-rose-500 flex items-center justify-center mx-auto">
                            <LogOut className="w-6 h-6" />
                        </div>
                        
                        <div className="text-center space-y-1">
                            <h3 className="text-base font-bold text-slate-900 dark:text-white">
                                Sign Out?
                            </h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                Are you sure you want to sign out of your Batcheet account?
                            </p>
                        </div>

                        <div className="flex items-center gap-2 pt-2">
                            <button
                                onClick={() => setShowSignOutConfirm(false)}
                                className="flex-1 py-2.5 px-4 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-bold transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={signOut}
                                className="flex-1 py-2.5 px-4 bg-rose-500 hover:bg-rose-600 text-white rounded-xl text-xs font-bold transition-all shadow-sm shadow-rose-500/20"
                            >
                                Confirm Sign Out
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default ProfilePage;